import React from "react";
import { ColumnDescription, ColumnFormatter } from "react-bootstrap-table-next";
import Table, { ITableProps, ITableView } from "..";
import { formatMoneyReais } from "../../../Tools/FormatTools";
import { groupBy } from "../../../Tools/ProcessingTools";
import HTMLRemover from "../../Utils/HTMLRemover";
import LeiaMais from "../../Utils/LeiaMais";

interface ICustomFormat<R, E = any, C = any> {
    name: string;
    formatter?: ColumnFormatter<R, E, C>;
    wrapperFormatter?: ColumnFormatter<R, E, C>;
    dataFormatter?: ColumnFormatter<R, E, C>;
}
type TFormat = "money" | "date" | "text" | "bigText" | "html";

const getDefaultFormatter = (
    f: TFormat | undefined,
    options: {
        bigTextMaxChar?: number;
    }
) => {
    const defaultFormatter = {
        money: formatMoneyReais,
        date: (c: any) => (c ? new Date(c).toLocaleDateString() : "-"),
        text: undefined,
        bigText: (c: any) => (
            <LeiaMais charLimit={options.bigTextMaxChar ?? 75}>{c}</LeiaMais>
        ),
        html: (c: any) => (
            <HTMLRemover charLimit={options.bigTextMaxChar ?? 75}>
                {c}
            </HTMLRemover>
        ),
    };

    if (f) return defaultFormatter[f];
    else return undefined;
};

export interface IDataTableColumn<T extends object = any, E = any>
    extends ColumnDescription<T, E> {
    // ignorado se for passado o formatter
    formatoPadrao?: TFormat;
    cardCustomPadrao?: string;
    // maximo de chars se for do tipo bigText
    bigTextMaxChar?: number;

    // customização em cardView, podendo ser 'completa' ( criar view do 0 )
    // ou 'wrapper' ( já vem com título, só editar o wrapper do dado em si )
    cardCustomFormatterType?: "completo" | "wrapper"; // default wrapper
    cardCustomFormatter?: ColumnFormatter<T, E>;
    cardCustomFormatterWrapper?: ColumnFormatter<T, E>;

    cardRow?: string;
}

interface IDataTableProps<T extends object = any>
    extends Omit<ITableProps, "tableView"> {
    columns: IDataTableColumn<T>[];

    cardHeader?: string;
    cardRows?: Array<{
        key: string;
        justify?: "center" | "between" | "evenly";
    }>;

    customs?: Array<ICustomFormat<T>>;

    defaultTableCellClasses?:
        | string
        | ((
              cell: T[keyof T],
              row: T,
              rowIndex: number,
              colIndex: number
          ) => string)
        | undefined;
    tableView?: Partial<ITableView>;
}

export function DataTable<T extends object = any>(props: IDataTableProps<T>) {
    const columnsGroupByRow = groupBy(props.columns, (c) => c.cardRow, true);
    const columnsGroupByRowArray = Array.from(columnsGroupByRow.entries());
    columnsGroupByRowArray.sort(
        (a, b) =>
            (props.cardRows?.findIndex((cr) => a[0] === cr.key) ?? -1) -
            (props.cardRows?.findIndex((cr) => b[0] === cr.key) ?? -1)
    );

    const columns: Array<ColumnDescription> = [
        {
            dataField: props.keyField,
            text: props.cardHeader ?? "",
            isDummyField: true,

            formatter: (...formatterArgs) => (
                <div className="w-100 ml-auto mb-2">
                    <div className="w-100 p-3">
                        {Array.from(columnsGroupByRow.entries()).map(
                            ([cardRow, columns]) => {
                                const row = props.cardRows?.find(
                                    (cr) => cr.key === cardRow
                                );
                                const [, ...restFormatterArgs] = formatterArgs;
                                return (
                                    <div
                                        key={cardRow ?? "key"}
                                        className={`d-flex flex-wrap align-itens-center justify-content-${
                                            row?.justify ?? "center"
                                        } mb-2 g-4`}
                                    >
                                        {columns.map((c) => {
                                            let column =
                                                /*row*/ restFormatterArgs[0][
                                                    c.dataField
                                                ];
                                            const fArgs: [
                                                row: any,
                                                rowIndex: number,
                                                formatExtraData: any
                                            ] = [
                                                restFormatterArgs[0],
                                                1,
                                                c.formatExtraData,
                                            ];
                                            const cardCustomPadrao =
                                                props.customs?.find(
                                                    (cs) =>
                                                        cs.name ===
                                                        c.cardCustomPadrao
                                                );
                                            const dataFormatter =
                                                cardCustomPadrao?.dataFormatter ??
                                                getDefaultFormatter(
                                                    c.formatoPadrao,
                                                    {
                                                        bigTextMaxChar:
                                                            c.bigTextMaxChar,
                                                    }
                                                );
                                            const wapperFormatter =
                                                c.cardCustomFormatterWrapper ??
                                                cardCustomPadrao?.wrapperFormatter;
                                            if (c.formatter)
                                                column = c.formatter(
                                                    column,
                                                    ...fArgs
                                                );
                                            if (dataFormatter)
                                                column = dataFormatter(
                                                    column,
                                                    ...fArgs
                                                );
                                            if (wapperFormatter)
                                                column = wapperFormatter(
                                                    column,
                                                    ...fArgs
                                                );
                                            const formatterCompleto =
                                                cardCustomPadrao?.formatter ??
                                                c.cardCustomFormatter;

                                            return (
                                                <React.Fragment
                                                    key={c.dataField}
                                                >
                                                    {formatterCompleto ? (
                                                        formatterCompleto(
                                                            column,
                                                            ...fArgs
                                                        )
                                                    ) : (
                                                        <div className="d-flex flex-column align-items-center mb-1">
                                                            <span className="text-dark-50 flex-root font-weight-bold">
                                                                {c.text}
                                                            </span>
                                                            <span className="text-dark flex-root font-weight-bold">
                                                                {column}
                                                            </span>
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
            ),
        },
    ];

    // para pesquisa
    // if(!props.noSearch)
    //     columns.push( ...props.columns.map( c => ({
    //         ...c,
    //         hidden: true,
    //         dataField: c.dataField,
    //         text: ""
    //     })))

    const tableViewColumns = props.columns.map((c) => {
        const dataFormatterCustom = props.customs?.find(
            (cs) => cs.name === c.cardCustomPadrao
        )?.dataFormatter;
        const dataFormatterPadrao = getDefaultFormatter(c.formatoPadrao, {
            bigTextMaxChar: c.bigTextMaxChar,
        });
        return {
            ...c,
            formatter: (...fArgs: [any, T, number, any]) => {
                if (c.formatter) fArgs[0] = c.formatter(...fArgs);
                if (dataFormatterPadrao)
                    fArgs[0] = dataFormatterPadrao(fArgs[0]);
                if (dataFormatterCustom)
                    fArgs[0] = dataFormatterCustom(...fArgs);
                return fArgs[0];
            },
            classes: c.classes ?? props.defaultTableCellClasses,
            sort: c.sort ?? true,
            headerClasses: `${c.headerClasses} ${
                c.sort ?? true ? "cursor-pointer" : ""
            }`,
            align: c.align ?? "center",
            headerAlign: c.headerAlign ?? "center",
        };
    });

    return (
        <Table
            {...props}
            columns={columns}
            tableView={{
                ...props.tableView,
                columns: tableViewColumns,
            }}
        />
    );
}
